import { CardType, Faction } from '../../types'
import { deckStats } from '../../store'

interface DeckStatsProps {
  formatRules?: {
    maxCards: number
    maxCopies: number
    maxReserve: number
    allowedFactions: Faction[]
  }
}

const TYPE_COLORS: Record<CardType, string> = {
  [CardType.UNIT]: 'bg-blue-500',
  [CardType.SPELL]: 'bg-purple-500',
  [CardType.ARTIFACT]: 'bg-yellow-500',
  [CardType.LEADER]: 'bg-red-500',
}

const FACTION_COLORS: Record<Faction, string> = {
  [Faction.HUMAN]: '#3b82f6',
  [Faction.DEMON]: '#ef4444',
  [Faction.ELF]: '#22c55e',
  [Faction.DRAGON]: '#f97316',
  [Faction.UNDEAD]: '#6b7280',
  [Faction.NEUTRAL]: '#a855f7',
}

export function DeckStats({ formatRules }: DeckStatsProps) {
  const stats = deckStats()

  if (!stats) {
    return (
      <div className="p-4 bg-gray-800 rounded-lg">
        <p className="text-gray-400 text-sm">No hay cartas en el mazo</p>
      </div>
    )
  }

  const totalCards = stats.totalCards
  const maxCards = formatRules?.maxCards || 60

  const typeEntries = Object.entries(stats.byType).sort((a, b) => b[1] - a[1])
  const factionEntries = Object.entries(stats.byFaction).sort((a, b) => b[1] - a[1])

  return (
    <div className="space-y-4">
      <div className="bg-gray-800 rounded-lg p-4">
        <h3 className="text-white font-semibold mb-3">Resumen</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-700 rounded p-2">
            <p className="text-gray-400 text-xs">Total</p>
            <p className="text-white text-xl font-bold">{totalCards}/{maxCards}</p>
          </div>
          <div className="bg-gray-700 rounded p-2">
            <p className="text-gray-400 text-xs">Costo Promedio</p>
            <p className="text-white text-xl font-bold">{stats.avgCost.toFixed(1)}</p>
          </div>
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg p-4">
        <h3 className="text-white font-semibold mb-3">Por Tipo</h3>
        <div className="space-y-2">
          {typeEntries.map(([type, count]) => {
            const percentage = totalCards > 0 ? (count / totalCards) * 100 : 0
            return (
              <div key={type}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-300">{type}</span>
                  <span className="text-white">{count}</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${TYPE_COLORS[type as CardType] || 'bg-gray-500'}`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg p-4">
        <h3 className="text-white font-semibold mb-3">Por Facción</h3>
        <div className="space-y-2">
          {factionEntries.map(([faction, count]) => {
            const percentage = totalCards > 0 ? (count / totalCards) * 100 : 0
            return (
              <div key={faction}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-300">{faction}</span>
                  <span className="text-white">{count}</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full transition-all"
                    style={{
                      width: `${percentage}%`,
                      backgroundColor: FACTION_COLORS[faction as Faction] || '#6b7280'
                    }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {totalCards > maxCards && (
        <div className="bg-red-900/30 border border-red-700 rounded-lg p-3">
          <p className="text-red-400 text-sm font-medium">
            ⚠️ El mazo tiene {totalCards - maxCards} cartas de más
          </p>
        </div>
      )}

      {formatRules && (
        <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-3">
          <h4 className="text-yellow-400 text-sm font-medium mb-2">Reglas del Formato</h4>
          <ul className="text-xs text-gray-300 space-y-1">
            <li>Máximo: {formatRules.maxCards} cartas</li>
            <li>Máx. copias: {formatRules.maxCopies}</li>
            <li>Reserva máx: {formatRules.maxReserve}</li>
          </ul>
        </div>
      )}
    </div>
  )
}
